import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, ShieldCheck, Car, ChevronDown } from "lucide-react";
import { IMAGES } from "@/data/siteData";

const TRUST = [
  { icon: Sparkles, title: "100% Customized Holidays", desc: "Tailored to your dates, pace & dreams" },
  { icon: ShieldCheck, title: "Local Goa Assistance", desc: "On-ground team, a call away, 24x7" },
  { icon: Car, title: "Convenient Transport", desc: "Airport pickup, private taxis & transfers" },
];

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 0.35 + i * 0.16, duration: 0.95, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Golden Goa beach at sunset with swaying palm trees"
          className="w-full h-full object-cover scale-110"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-abyss/55 via-navy-abyss/15 to-navy-abyss" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-abyss/55 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 pt-32">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="text-[11px] sm:text-xs uppercase tracking-[0.32em] text-gold font-semibold mb-5 sm:mb-7"
          data-testid="hero-eyebrow"
        >
          RAJ TRAVELS GOA — Premium Goa Holidays
        </motion.p>

        <h1 className="font-display font-bold text-4xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight max-w-4xl" data-testid="hero-headline">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
              Your Dream Goa
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block gold-text italic">
              Holiday Starts Here!
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-5 sm:mt-6 max-w-2xl text-sm sm:text-base lg:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Discover beautiful beaches, romantic escapes, family holidays, thrilling adventures and
          unforgettable Goa nightlife with RAJ TRAVELS GOA.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
        >
          <a
            href="#packages"
            data-testid="hero-explore-packages-btn"
            className="inline-flex items-center justify-center bg-gold text-navy-abyss font-semibold px-8 py-4 rounded-full text-sm sm:text-base hover:bg-gold-hover hover:shadow-[0_0_36px_rgba(212,175,55,0.45)] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5"
          >
            Explore Goa Packages
          </a>
          <a
            href="#enquiry"
            data-testid="hero-plan-trip-btn"
            className="inline-flex items-center justify-center border border-gold/50 text-gold-light font-medium px-8 py-4 rounded-full text-sm sm:text-base hover:bg-gold/10 hover:border-gold transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5"
          >
            Plan My Goa Trip
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
          className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5 max-w-4xl"
          data-testid="hero-trust-highlights"
        >
          {TRUST.map((t) => (
            <div
              key={t.title}
              className="flex sm:flex-col items-center sm:items-start gap-3 sm:gap-3 rounded-2xl border border-[#DC2626]/25 bg-white/60 backdrop-blur-md px-4 py-3.5 sm:px-5 sm:py-5"
            >
              <t.icon className="w-5 h-5 sm:w-6 sm:h-6 text-gold shrink-0" />
              <div>
                <p className="text-sm font-semibold text-ivory">{t.title}</p>
                <p className="text-xs text-slate-400 mt-0.5">{t.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.a
        href="#packages"
        aria-label="Scroll to packages"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-5 left-1/2 -translate-x-1/2 text-gold/70 hidden sm:block"
      >
        <ChevronDown className="w-6 h-6 animate-bounce" />
      </motion.a>
    </section>
  );
}
